// ============ LAYOUT DEFAULT TEMPLATES ============
// head, header, navmenu, footer — the skeleton of every theme

export function headTemplate(): string {
  return [
    '<title>{{site_name}} — {{site_description}}</title>',
    '<meta charset="UTF-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1.0">',
    '<meta name="description" content="{{site_description}}">',
    '<meta name="keywords" content="services, produits, entreprise, e-commerce, marketplace">',
    '<meta name="author" content="{{site_name}}">',
    '<meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1">',
    '<meta name="theme-color" content="#1a1817">',
    '',
    '<!-- Open Graph / Facebook -->',
    '<meta property="og:type" content="website">',
    '<meta property="og:url" content="{{site_url}}">',
    '<meta property="og:title" content="{{site_name}} — {{site_description}}">',
    '<meta property="og:description" content="{{site_description}}">',
    '<meta property="og:image" content="{{site_url}}/og-image.jpg">',
    '<meta property="og:image:width" content="1200">',
    '<meta property="og:image:height" content="630">',
    '<meta property="og:locale" content="fr_FR">',
    '<meta property="og:site_name" content="{{site_name}}">',
    '',
    '<!-- Twitter -->',
    '<meta name="twitter:card" content="summary_large_image">',
    '<meta name="twitter:url" content="{{site_url}}">',
    '<meta name="twitter:title" content="{{site_name}} — {{site_description}}">',
    '<meta name="twitter:description" content="{{site_description}}">',
    '<meta name="twitter:image" content="{{site_url}}/og-image.jpg">',
    '',
    '<!-- Canonical & Alternates -->',
    '<link rel="canonical" href="{{site_url}}">',
    '',
    '<!-- DNS Prefetch & Preconnect -->',
    '<link rel="dns-prefetch" href="https://fonts.googleapis.com">',
    '<link rel="dns-prefetch" href="https://fonts.gstatic.com">',
    '<link rel="preconnect" href="https://fonts.googleapis.com">',
    '<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>',
    '',
    '<!-- Google Fonts -->',
    '<link href="https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,400;0,14..32,500;0,14..32,600;0,14..32,700;1,14..32,400&family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&display=swap" rel="stylesheet">',
    '',
    '<!-- Stylesheet -->',
    '<link rel="stylesheet" href="/style.css">',
    '',
    '<!-- Favicon -->',
    '<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png">',
    '<link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png">',
    '<link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png">',
    '<link rel="manifest" href="/site.webmanifest">',
    '',
    '<!-- Structured Data -->',
    '<script type="application/ld+json">',
    '{',
    '  "@context": "https://schema.org",',
    '  "@type": "Organization",',
    '  "name": "{{site_name}}",',
    '  "description": "{{site_description}}",',
    '  "url": "{{site_url}}",',
    '  "telephone": "{{site_phone}}",',
    '  "email": "{{site_email}}",',
    '  "address": {',
    '    "@type": "PostalAddress",',
    '    "streetAddress": "{{site_address}}"',
    '  }',
    '}',
    '</script>',
  ].join('\n');
}

export function headerTemplate(): string {
  return [
    '<header class="site-header" role="banner">',
    '  <div class="container">',
    '    <a href="/" class="logo" aria-label="{{site_name}} — Retour à l\'accueil">',
    '      {{site_logo}}',
    '    </a>',
    '',
    '    <nav class="main-nav" aria-label="Navigation principale" role="navigation">',
    '      <ul class="nav-list">',
    '        <li><a href="/" class="nav-link active">Accueil</a></li>',
    '        <li><a href="/produits" class="nav-link">Boutique</a></li>',
    '        <li><a href="/service" class="nav-link">Services</a></li>',
    '        <li><a href="/apropos" class="nav-link">À propos</a></li>',
    '        <li><a href="/blog" class="nav-link">Blog</a></li>',
    '        <li><a href="/contact" class="nav-link">Contact</a></li>',
    '      </ul>',
    '    </nav>',
    '',
    '    <div class="header-actions">',
    '      <div class="social-icons" aria-label="Réseaux sociaux">',
    '        <a href="#" class="social-link" aria-label="Facebook" rel="nofollow noopener" target="_blank">',
    '          <i class="ri-facebook-fill"></i>',
    '        </a>',
    '        <a href="#" class="social-link" aria-label="Instagram" rel="nofollow noopener" target="_blank">',
    '          <i class="ri-instagram-line"></i>',
    '        </a>',
    '        <a href="#" class="social-link" aria-label="Twitter / X" rel="nofollow noopener" target="_blank">',
    '          <i class="ri-twitter-x-line"></i>',
    '        </a>',
    '        <a href="#" class="social-link" aria-label="LinkedIn" rel="nofollow noopener" target="_blank">',
    '          <i class="ri-linkedin-fill"></i>',
    '        </a>',
    '      </div>',
    '      <a href="/connexion" class="btn btn-outline btn-sm">',
    '        <i class="ri-user-line"></i>',
    '        <span>Connexion</span>',
    '      </a>',
    '    </div>',
    '',
    '    <button class="hamburger" aria-label="Menu" aria-expanded="false" aria-controls="mobile-menu">',
    '      <span class="hamburger-line"></span>',
    '      <span class="hamburger-line"></span>',
    '      <span class="hamburger-line"></span>',
    '    </button>',
    '  </div>',
    '</header>',
  ].join('\n');
}

export function navmenuTemplate(): string {
  return [
    '<nav class="navmenu" aria-label="Menu principal" role="navigation" id="mobile-menu">',
    '  <div class="container">',
    '    <ul class="navmenu-list">',
    '      <li><a href="/" class="navmenu-link">Accueil</a></li>',
    '      <li class="has-dropdown">',
    '        <a href="/produits" class="navmenu-link">',
    '          <span>Boutique</span>',
    '          <i class="ri-arrow-down-s-line dropdown-arrow"></i>',
    '        </a>',
    '        <div class="dropdown-menu">',
    '          <div class="dropdown-grid">',
    '            <div class="dropdown-col">',
    '              <span class="dropdown-label">Catégories</span>',
    '              {{marketplace_categories}}',
    '            </div>',
    '            <div class="dropdown-col">',
    '              <span class="dropdown-label">En vedette</span>',
    '              {{featured_products limit="4"}}',
    '            </div>',
    '          </div>',
    '        </div>',
    '      </li>',
    '      <li><a href="/service" class="navmenu-link">Services</a></li>',
    '      <li><a href="/apropos" class="navmenu-link">À propos</a></li>',
    '      <li><a href="/blog" class="navmenu-link">Blog</a></li>',
    '      <li><a href="/contact" class="navmenu-link">Contact</a></li>',
    '    </ul>',
    '',
    '    <div class="navmenu-social mobile-only">',
    '      <span class="navmenu-social-label">Suivez-nous</span>',
    '      <div class="navmenu-social-icons">',
    '        <a href="#" class="social-link" aria-label="Facebook" rel="nofollow noopener" target="_blank">',
    '          <i class="ri-facebook-fill"></i>',
    '        </a>',
    '        <a href="#" class="social-link" aria-label="Instagram" rel="nofollow noopener" target="_blank">',
    '          <i class="ri-instagram-line"></i>',
    '        </a>',
    '        <a href="#" class="social-link" aria-label="Twitter / X" rel="nofollow noopener" target="_blank">',
    '          <i class="ri-twitter-x-line"></i>',
    '        </a>',
    '        <a href="#" class="social-link" aria-label="LinkedIn" rel="nofollow noopener" target="_blank">',
    '          <i class="ri-linkedin-fill"></i>',
    '        </a>',
    '      </div>',
    '    </div>',
    '  </div>',
    '</nav>',
  ].join('\n');
}

export function footerTemplate(): string {
  return [
    '<footer class="site-footer" role="contentinfo">',
    '  <div class="container">',
    '',
    '    <!-- Newsletter -->',
    '    <div class="footer-newsletter">',
    '      <div class="footer-newsletter-content">',
    '        <h4>Restez informé</h4>',
    '        <p>Recevez nos actualités, offres et nouveautés directement dans votre boîte mail.</p>',
    '      </div>',
    '      {{contact_form}}',
    '    </div>',
    '',
    '    <!-- Main grid -->',
    '    <div class="footer-grid">',
    '      <!-- Brand -->',
    '      <div class="footer-col footer-col-brand">',
    '        <div class="footer-brand">',
    '          {{site_logo}}',
    '          <span class="footer-brand-name">{{site_name}}</span>',
    '        </div>',
    '        <p class="footer-description">{{site_description}}</p>',
    '        <div class="footer-social">',
    '          <a href="#" class="social-link" aria-label="Facebook" rel="nofollow noopener" target="_blank">',
    '            <i class="ri-facebook-fill"></i>',
    '          </a>',
    '          <a href="#" class="social-link" aria-label="Instagram" rel="nofollow noopener" target="_blank">',
    '            <i class="ri-instagram-line"></i>',
    '          </a>',
    '          <a href="#" class="social-link" aria-label="Twitter / X" rel="nofollow noopener" target="_blank">',
    '            <i class="ri-twitter-x-line"></i>',
    '          </a>',
    '          <a href="#" class="social-link" aria-label="LinkedIn" rel="nofollow noopener" target="_blank">',
    '            <i class="ri-linkedin-fill"></i>',
    '          </a>',
    '        </div>',
    '      </div>',
    '',
    '      <!-- Liens rapides -->',
    '      <div class="footer-col">',
    '        <h5 class="footer-col-title">Navigation</h5>',
    '        <ul class="footer-links">',
    '          <li><a href="/">Accueil</a></li>',
    '          <li><a href="/produits">Boutique</a></li>',
    '          <li><a href="/service">Services</a></li>',
    '          <li><a href="/apropos">À propos</a></li>',
    '          <li><a href="/blog">Blog</a></li>',
    '          <li><a href="/contact">Contact</a></li>',
    '        </ul>',
    '      </div>',
    '',
    '      <!-- Services -->',
    '      <div class="footer-col">',
    '        <h5 class="footer-col-title">Nos services</h5>',
    '        {{services}}',
    '      </div>',
    '',
    '      <!-- Contact -->',
    '      <div class="footer-col">',
    '        <h5 class="footer-col-title">Contact</h5>',
    '        <ul class="footer-contact">',
    '          <li>',
    '            <i class="ri-phone-line"></i>',
    '            <a href="tel:{{site_phone}}">{{site_phone}}</a>',
    '          </li>',
    '          <li>',
    '            <i class="ri-mail-line"></i>',
    '            <a href="mailto:{{site_email}}">{{site_email}}</a>',
    '          </li>',
    '          <li>',
    '            <i class="ri-map-pin-line"></i>',
    '            <span>{{site_address}}</span>',
    '          </li>',
    '        </ul>',
    '      </div>',
    '',
    '      <!-- Légal -->',
    '      <div class="footer-col">',
    '        <h5 class="footer-col-title">Légal</h5>',
    '        <ul class="footer-links">',
    '          <li><a href="/conditionsdutilisation">Conditions d\'utilisation</a></li>',
    '          <li><a href="/politique">Politique de confidentialité</a></li>',
    '          <li><a href="/retours">Politique de retours</a></li>',
    '          <li><a href="/contact">Nous contacter</a></li>',
    '        </ul>',
    '      </div>',
    '    </div>',
    '',
    '    <!-- Bottom bar -->',
    '    <div class="footer-bottom">',
    '      <div class="footer-bottom-left">',
    '        <p>&copy; {{year}} {{site_name}}. Tous droits réservés.</p>',
    '      </div>',
    '      <div class="footer-bottom-right">',
    '        <span class="footer-payment-label">Paiements sécurisés</span>',
    '        <div class="footer-payment-icons">',
    '          <i class="ri-visa-line" title="Visa"></i>',
    '          <i class="ri-mastercard-line" title="Mastercard"></i>',
    '          <i class="ri-paypal-line" title="PayPal"></i>',
    '        </div>',
    '      </div>',
    '    </div>',
    '',
    '  </div>',
    '</footer>',
  ].join('\n');
}